import { StatBonus } from '../types/progression.types';
import { PlayerEntity } from '../entities/PlayerEntity';
import { DEFAULT_PLAYER_CONFIG } from '../data/player.config';
import {
  PLAYER_LEVEL_STAT_RATES,
  playerStatAtLevel,
} from '../data/progression.config';
import type { IEntityStats } from '../types/entity.types';

export class IntraRunProgressionService {
  private static instance: IntraRunProgressionService;
  /** Injected by DungeonService after both singletons are created. */
  public dungeonService: { markNeedsRender(): void } | null = null;

  private constructor() {}

  public static getInstance(): IntraRunProgressionService {
    if (!IntraRunProgressionService.instance) {
      IntraRunProgressionService.instance = new IntraRunProgressionService();
    }
    return IntraRunProgressionService.instance;
  }

  // Writes to baseStats, otherwise the next tick's recomputeStats would
  // overwrite the value.
  public applyBonus(player: PlayerEntity, bonus: StatBonus): void {
    for (const [key, value] of Object.entries(bonus)) {
      const n = value as number;
      if (key === 'maxLife') {
        player.baseStats.maxLife += n;
        player.maxLife += n;
        player.life = Math.min(player.life + n, player.maxLife);
      } else if (key === 'maxShield') {
        player.baseStats.maxShield += n;
        player.maxShield += n;
      } else if (key in player.baseStats) {
        (player.baseStats as unknown as Record<string, number>)[key] += n;
      }
    }
    this.dungeonService?.markNeedsRender();
  }

  // Applies the passive stat gain for crossing from `fromLevel` to `toLevel`.
  // The gain is the delta of the per-level scaled base (same curve as the
  // run-start bake), so leveling mid-run matches what the next fresh bake would
  // produce. Returns the applied bonus (consumed by the level-up snackbar).
  public applyLevelUps(
    player: PlayerEntity,
    fromLevel: number,
    toLevel: number,
  ): StatBonus {
    const bonus: StatBonus = {};
    for (const key of Object.keys(
      PLAYER_LEVEL_STAT_RATES,
    ) as (keyof IEntityStats)[]) {
      const rate = PLAYER_LEVEL_STAT_RATES[key];
      if (rate === undefined) {
        continue;
      }
      const base = DEFAULT_PLAYER_CONFIG.stats[key] as number;
      const delta =
        playerStatAtLevel(base, toLevel, rate) -
        playerStatAtLevel(base, fromLevel, rate);
      if (delta !== 0) {
        (bonus as Record<string, number>)[key] = delta;
      }
    }
    this.applyBonus(player, bonus);
    return bonus;
  }
}

export const intraRunProgressionService =
  IntraRunProgressionService.getInstance();
